import { defineNuxtPlugin, useAsyncData, useRoute } from 'nuxt/app';
import { getPageSlugFast } from '@/composables/useApiSlugs';
import { fetchSettingsWithRetry } from '@/composables/useSettingsFetch';
import { getCachedDataWithServerPrefetch } from '@/composables/useServerPrefetchCache';
import { useGlobalStore } from '@/stores/global';

/**
 * Make initial requests for common data
 * @description Renamed from 'initial-data.server.js' due to plugin not start in pages with routeRules: 'ssr: false'
 * @description Check for flag in stores to avoid unnecessary requests on client e.g. 'isFetched'
 */
export default defineNuxtPlugin({
  parallel: true,
  async setup() {
    const requests = [];

    if (process.server) {
      const route = useRoute();
      const globalStore = useGlobalStore();

      // Kicked off together (not one `await`ed before the next) so the
      // settings fetch and the page's own CMS fetch run concurrently on the
      // backend, instead of the sequential waterfall Vue's SSR otherwise
      // forces: a parent component's async setup (layouts/default.vue's
      // settings fetch) must fully resolve before Vue even instantiates its
      // children, so pages/index.vue's useCmsPage fetch couldn't previously
      // start until settings had already finished.
      requests.push(
        useAsyncData('globalSettings', () => fetchSettingsWithRetry(), {
          server: true,
          getCachedData: getCachedDataWithServerPrefetch,
        }),
      );

      // Catalog routes (/cars, /used-cars, /cars-in-stock, /buses) need
      // async filter tokens to resolve their slug (see
      // useApiSlugs.getPageSlug) — getPageSlugFast returns null for those
      // and we simply don't prefetch, leaving them on the untouched
      // sequential path. /thanks doesn't use useCmsPage at all.
      const isThanksPage =
        route.name === 'thanks' || route.path.includes('/thanks');
      const slug = isThanksPage ? null : getPageSlugFast(route.path);

      if (slug) {
        const lang = route.params.lang || 'uk';
        requests.push(
          useAsyncData(
            `cms-page-${lang}-${slug}`,
            () => globalStore.fetchPageData(slug),
            { server: true, getCachedData: getCachedDataWithServerPrefetch },
          ),
        );
      }
    }

    await Promise.all(requests);
  },
});
